import { useState, useEffect } from "react";
import { FileText, Users, Filter, Eye, Check, X, Calendar, Building2, Mail, Phone, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InquiriesManager = () => {
  const [consultingInquiries, setConsultingInquiries] = useState([]);
  const [trainingInquiries, setTrainingInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consultingFilter, setConsultingFilter] = useState("all");
  const [trainingFilter, setTrainingFilter] = useState("all");
  const [expandedInquiry, setExpandedInquiry] = useState(null);
  
  const { getAuthHeader } = useAuth();

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const authHeader = getAuthHeader();
      const [consultingRes, trainingRes] = await Promise.all([
        axios.get(`${API}/consulting-inquiries`, { headers: authHeader }),
        axios.get(`${API}/inhouse-training-inquiries`, { headers: authHeader })
      ]);
      
      setConsultingInquiries(consultingRes.data.inquiries || []);
      setTrainingInquiries(trainingRes.data.inquiries || []);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const updateInquiryStatus = async (type, id, newStatus) => {
    try {
      const authHeader = getAuthHeader();
      const endpoint = type === "consulting" 
        ? `${API}/consulting-inquiries/${id}/status`
        : `${API}/inhouse-training-inquiries/${id}/status`;
      
      await axios.patch(endpoint, { status: newStatus }, { headers: authHeader });
      
      if (type === "consulting") {
        setConsultingInquiries(prev => 
          prev.map(i => i.id === id ? { ...i, status: newStatus } : i)
        );
      } else {
        setTrainingInquiries(prev => 
          prev.map(i => i.id === id ? { ...i, status: newStatus } : i)
        );
      }
      
      toast.success(`Inquiry marked as ${newStatus}`);
    } catch (error) {
      toast.error("Failed to update inquiry status");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-blue-100 text-blue-800",
      contacted: "bg-yellow-100 text-yellow-800",
      in_progress: "bg-purple-100 text-purple-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-gray-100 text-gray-800"
    };
    return styles[status] || styles.new;
  };

  const filterInquiries = (inquiries, filter) => {
    if (filter === "all") return inquiries;
    return inquiries.filter(i => i.status === filter);
  };

  const ConsultingInquiryCard = ({ inquiry }) => {
    const isExpanded = expandedInquiry === `consulting-${inquiry.id}`;
    
    return (
      <Card className={`mb-4 ${inquiry.status === 'new' ? 'border-l-4 border-l-brand' : ''}`}>
        <CardContent className="p-4">
          <div 
            className="flex items-start justify-between cursor-pointer"
            onClick={() => setExpandedInquiry(isExpanded ? null : `consulting-${inquiry.id}`)}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold">{inquiry.name}</h4>
                <Badge className={getStatusBadge(inquiry.status)}>
                  {inquiry.status?.replace('_', ' ') || 'new'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Building2 className="h-3 w-3" />
                {inquiry.organization}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(inquiry.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </div>
          
          {isExpanded && (
            <div className="mt-4 pt-4 border-t space-y-3">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    {inquiry.email}
                  </p>
                  {inquiry.phone && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                      <Phone className="h-3 w-3" />
                      {inquiry.phone}
                    </p>
                  )}
                </div>
                <div>
                  {inquiry.industry && (
                    <p className="text-sm"><strong>Industry:</strong> {inquiry.industry}</p>
                  )}
                  {inquiry.preferred_dates && (
                    <p className="text-sm flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <strong>Preferred Dates:</strong> {inquiry.preferred_dates}
                    </p>
                  )}
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium mb-1">Services Requested:</p>
                <p className="text-sm bg-slate-50 p-2 rounded">{inquiry.services}</p>
              </div>
              
              {inquiry.description && (
                <div>
                  <p className="text-sm font-medium mb-1">Additional Details:</p>
                  <p className="text-sm bg-slate-50 p-2 rounded">{inquiry.description}</p>
                </div>
              )}
              
              <div className="flex gap-2 pt-2">
                <Select
                  value={inquiry.status || "new"}
                  onValueChange={(value) => updateInquiryStatus("consulting", inquiry.id, value)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" asChild>
                  <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${inquiry.email}&su=Re: Consulting Inquiry from ${encodeURIComponent(inquiry.organization)}&from=info@comply-cms.com`} target="_blank" rel="noopener noreferrer">
                    <Mail className="h-4 w-4 mr-1" />
                    Reply
                  </a>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const TrainingInquiryCard = ({ inquiry }) => {
    const isExpanded = expandedInquiry === `training-${inquiry.id}`;
    
    return (
      <Card className={`mb-4 ${inquiry.status === 'new' ? 'border-l-4 border-l-brand' : ''}`}>
        <CardContent className="p-4">
          <div 
            className="flex items-start justify-between cursor-pointer"
            onClick={() => setExpandedInquiry(isExpanded ? null : `training-${inquiry.id}`)}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold">{inquiry.contact_name}</h4>
                <Badge className={getStatusBadge(inquiry.status)}>
                  {inquiry.status?.replace('_', ' ') || 'new'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Building2 className="h-3 w-3" />
                {inquiry.organization}
                {inquiry.participants && (
                  <span className="ml-2 flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {inquiry.participants}
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(inquiry.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </div>
          
          {isExpanded && (
            <div className="mt-4 pt-4 border-t space-y-3">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    {inquiry.email}
                  </p>
                  {inquiry.phone && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                      <Phone className="h-3 w-3" />
                      {inquiry.phone}
                    </p>
                  )}
                </div>
                <div>
                  {inquiry.industry && (
                    <p className="text-sm"><strong>Industry:</strong> {inquiry.industry}</p>
                  )}
                  {inquiry.preferred_format && (
                    <p className="text-sm"><strong>Format:</strong> {inquiry.preferred_format}</p>
                  )}
                  {inquiry.preferred_dates && (
                    <p className="text-sm flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <strong>Preferred Dates:</strong> {inquiry.preferred_dates}
                    </p>
                  )}
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium mb-1">Training Topics:</p>
                <p className="text-sm bg-slate-50 p-2 rounded">{inquiry.training_topics}</p>
              </div>
              
              {inquiry.description && (
                <div>
                  <p className="text-sm font-medium mb-1">Additional Details:</p>
                  <p className="text-sm bg-slate-50 p-2 rounded">{inquiry.description}</p>
                </div>
              )}
              
              <div className="flex gap-2 pt-2">
                <Select
                  value={inquiry.status || "new"}
                  onValueChange={(value) => updateInquiryStatus("training", inquiry.id, value)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" asChild>
                  <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${inquiry.email}&su=Re: In-House Training Inquiry from ${encodeURIComponent(inquiry.organization)}&from=info@comply-cms.com`} target="_blank" rel="noopener noreferrer">
                    <Mail className="h-4 w-4 mr-1" />
                    Reply
                  </a>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="h-4 w-1/3 skeleton rounded mb-2" />
              <div className="h-3 w-1/2 skeleton rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const newConsultingCount = consultingInquiries.filter(i => i.status === 'new' || !i.status).length;
  const newTrainingCount = trainingInquiries.filter(i => i.status === 'new' || !i.status).length;

  return (
    <div data-testid="inquiries-manager">
      <Tabs defaultValue="consulting" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="consulting" className="data-[state=active]:bg-brand data-[state=active]:text-white">
            <FileText className="h-4 w-4 mr-1" />
            Consulting
            {newConsultingCount > 0 && (
              <Badge className="ml-2 bg-orange-500 text-white">{newConsultingCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="training" className="data-[state=active]:bg-brand data-[state=active]:text-white">
            <Users className="h-4 w-4 mr-1" />
            In-House Training
            {newTrainingCount > 0 && (
              <Badge className="ml-2 bg-orange-500 text-white">{newTrainingCount}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="consulting">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg">
              Consulting Inquiries ({consultingInquiries.length})
            </h3>
            <Select value={consultingFilter} onValueChange={setConsultingFilter}>
              <SelectTrigger className="w-40">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {filterInquiries(consultingInquiries, consultingFilter).length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                No consulting inquiries found
              </CardContent>
            </Card>
          ) : (
            filterInquiries(consultingInquiries, consultingFilter).map(inquiry => (
              <ConsultingInquiryCard key={inquiry.id} inquiry={inquiry} />
            ))
          )}
        </TabsContent>

        <TabsContent value="training">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg">
              In-House Training Inquiries ({trainingInquiries.length})
            </h3>
            <Select value={trainingFilter} onValueChange={setTrainingFilter}>
              <SelectTrigger className="w-40">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {filterInquiries(trainingInquiries, trainingFilter).length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                No in-house training inquiries found
              </CardContent>
            </Card>
          ) : (
            filterInquiries(trainingInquiries, trainingFilter).map(inquiry => (
              <TrainingInquiryCard key={inquiry.id} inquiry={inquiry} />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InquiriesManager;
